import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight, Clock, ExternalLink, ListTree } from "lucide-react";
import { api } from "../lib/api";
import { setMeta } from "../lib/seo";
import { Rating } from "../components/Rating";
import { AdSlot } from "../components/AdSlot";

const sectionId = (h) => h.toLowerCase().replace(/[^a-z0-9]+/g, "-");

export default function BlogPostPage() {
  const { slug } = useParams();

  const { data: post, isLoading, isError } = useQuery({
    queryKey: ["blog-post", slug],
    queryFn: () => api.getBlogPost(slug),
    retry: false,
  });

  const { data: products = [] } = useQuery({
    queryKey: ["blog-products", slug],
    queryFn: () => Promise.all((post?.product_slugs || []).map((s) => api.getProduct(s).catch(() => null))).then((r) => r.filter(Boolean)),
    enabled: !!post,
  });

  const { data: allPosts = [] } = useQuery({ queryKey: ["blog", "all"], queryFn: api.getBlogPosts });
  const relatedPosts = allPosts.filter((p) => p.slug !== slug && p.tags?.some((t) => post?.tags?.includes(t))).slice(0, 3);

  useEffect(() => {
    if (post) setMeta(`${post.title} | ShoppinMagic Blog`, post.excerpt);
  }, [post]);

  if (isLoading) return <div className="mx-auto max-w-4xl px-4 py-20 text-center text-slate-500">Loading article…</div>;
  if (isError || !post) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-20 text-center" data-testid="post-not-found">
        <h1 className="font-heading text-2xl font-bold text-slate-900">Article not found</h1>
        <Link to="/blog" className="mt-4 inline-block rounded-xl bg-gold-600 px-6 py-3 text-sm font-bold text-white">Back to blog</Link>
      </div>
    );
  }

  return (
    <div data-testid="blog-post-page" className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <nav className="mb-5 flex items-center gap-1 text-xs text-slate-500" aria-label="Breadcrumb" data-testid="breadcrumbs">
        <Link to="/" className="hover:text-gold-600">Home</Link>
        <ChevronRight size={12} />
        <Link to="/blog" className="hover:text-gold-600">Blog</Link>
        <ChevronRight size={12} />
        <span className="line-clamp-1 max-w-[200px] font-semibold text-slate-700">{post.title}</span>
      </nav>

      <div className="flex gap-10">
        <article className="min-w-0 flex-1">
          <span className="text-xs font-bold uppercase tracking-wider text-gold-600">{post.category}</span>
          <h1 className="font-heading mt-2 text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl" data-testid="post-title">
            {post.title}
          </h1>
          <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-slate-500">
            <span className="font-semibold text-slate-700">{post.author}</span>
            <span>·</span>
            <span>{post.date}</span>
            <span>·</span>
            <span className="flex items-center gap-1"><Clock size={12} /> {post.read_time} min read</span>
          </div>

          <img src={post.image} alt={post.title} className="mt-6 aspect-[16/9] w-full rounded-2xl object-cover" data-testid="post-featured-image" />

          {/* Mobile TOC */}
          <div className="mt-6 rounded-xl border border-slate-200 bg-white p-4 lg:hidden" data-testid="toc-mobile">
            <p className="flex items-center gap-2 text-sm font-bold text-slate-900"><ListTree size={15} /> In this guide</p>
            <ul className="mt-2 space-y-1.5">
              {post.sections.map((s) => (
                <li key={s.heading}>
                  <a href={`#${sectionId(s.heading)}`} className="text-sm text-gold-600 hover:underline">{s.heading}</a>
                </li>
              ))}
            </ul>
          </div>

          {post.sections.map((s, i) => (
            <section key={s.heading} id={sectionId(s.heading)} className="mt-8 scroll-mt-24">
              <h2 className="font-heading text-xl font-bold text-slate-900 sm:text-2xl">{s.heading}</h2>
              <p className="mt-3 text-sm leading-relaxed text-slate-600 sm:text-base">{s.body}</p>
              {i === 1 && <AdSlot slot="in-content" minH="min-h-[120px]" className="mt-6" />}
            </section>
          ))}

          {/* Product recommendations */}
          {products.length > 0 && (
            <section className="mt-10" data-testid="post-product-recommendations">
              <h2 className="font-heading text-xl font-bold text-slate-900 sm:text-2xl">Products mentioned in this guide</h2>
              <div className="mt-4 space-y-4">
                {products.map((p) => {
                  const discount = Math.round(((p.original_price - p.price) / p.original_price) * 100);
                  return (
                    <div key={p.slug} className="flex flex-col gap-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:flex-row sm:items-center" data-testid={`post-product-${p.slug}`}>
                      <Link to={`/product/${p.slug}`} className="shrink-0">
                        <img src={p.images[0]} alt={p.title} className="h-28 w-28 rounded-lg object-cover" />
                      </Link>
                      <div className="min-w-0 flex-1">
                        <Link to={`/product/${p.slug}`} className="line-clamp-2 text-sm font-bold text-slate-900 hover:text-gold-600 sm:text-base">
                          {p.title}
                        </Link>
                        <div className="mt-1"><Rating value={p.rating} count={p.reviews_count} size={12} /></div>
                        <div className="mt-1 flex items-baseline gap-2">
                          <span className="text-lg font-black text-gold-600">${p.price.toFixed(2)}</span>
                          {discount > 0 && <span className="text-xs text-slate-400 line-through">${p.original_price.toFixed(2)}</span>}
                        </div>
                      </div>
                      <a
                        href={p.affiliate_url}
                        target="_blank"
                        rel="nofollow sponsored noopener"
                        data-testid={`post-affiliate-cta-${p.slug}`}
                        className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-gold-600 px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-gold-700"
                      >
                        View on {p.merchant} <ExternalLink size={14} />
                      </a>
                    </div>
                  );
                })}
              </div>
              <p className="mt-3 text-xs text-slate-400">
                Links above are affiliate links — we may earn a commission at no extra cost to you.{" "}
                <Link to="/legal/affiliate-disclosure" className="underline">Learn more</Link>.
              </p>
            </section>
          )}

          {/* Related posts */}
          {relatedPosts.length > 0 && (
            <section className="mt-10" data-testid="related-posts">
              <h2 className="font-heading text-xl font-bold text-slate-900 sm:text-2xl">Keep reading</h2>
              <div className="mt-4 grid gap-4 sm:grid-cols-3">
                {relatedPosts.map((p) => (
                  <Link key={p.slug} to={`/blog/${p.slug}`} data-testid={`related-post-${p.slug}`} className="group overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm hover:shadow-md">
                    <img src={p.image} alt={p.title} loading="lazy" className="aspect-[16/9] w-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    <div className="p-3">
                      <h3 className="line-clamp-2 text-sm font-bold text-slate-900 group-hover:text-gold-600">{p.title}</h3>
                      <p className="mt-1 text-xs text-slate-400">{p.read_time} min read</p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}

          <AdSlot slot="footer" minH="min-h-[90px]" className="mt-10" />
        </article>

        {/* Sidebar TOC + ad */}
        <aside className="hidden w-72 shrink-0 lg:block">
          <div className="sticky top-24 space-y-5">
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="toc-desktop">
              <p className="flex items-center gap-2 text-sm font-bold text-slate-900"><ListTree size={15} /> In this guide</p>
              <ul className="mt-3 space-y-2 border-l-2 border-slate-100 pl-3">
                {post.sections.map((s) => (
                  <li key={s.heading}>
                    <a href={`#${sectionId(s.heading)}`} className="text-sm text-slate-600 hover:text-gold-600">{s.heading}</a>
                  </li>
                ))}
              </ul>
            </div>
            <AdSlot slot="sidebar" minH="min-h-[300px]" />
          </div>
        </aside>
      </div>
    </div>
  );
}
